import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/endpoints';
import axios from 'axios';
import {
  DocumentPreviewResponse,
  DocumentsResponse,
  FileListResponse,
  FilePreviewResponse,
  parseSizeToBytes,
  PresignedUrlRequest,
  PresignedUrlResponse,
  UploadDocumentsRequest,
  UploadUrlResponse,
} from '../types/file.types';

const DEFAULT_CONTENT_TYPE = 'application/octet-stream';

function resolveDocumentId(response: UploadUrlResponse): string {
  if (response._id) return response._id;
  return response.key?.split('/').pop() ?? '';
}

export const fileService = {
  /**
   * Get list of uploaded documents (GET /api/docs – operationId: getDocuments).
   * Returns DocumentsResponse with files array; maps to FileListResponse.
   * @param taxYear - Tax year to list documents for (required by backend).
   */
  getDocuments: async (taxYear: number): Promise<FileListResponse> => {
    const response = await apiClient.get<DocumentsResponse>(API_ENDPOINTS.DOCS, {
      params: { taxYear },
    });
    const documents: FileListResponse['documents'] = response.data.files.map((doc) => ({
      id: doc._id,
      name: doc.name,
      size: parseSizeToBytes(doc.size),
      mimeType: doc.type ?? DEFAULT_CONTENT_TYPE,
      createdAt: doc.createdAt,
      userId: undefined,
      url: undefined,
    }));
    return {
      documents,
      total: documents.length,
    };
  },

  /**
   * Request presigned URLs for upload (POST /api/docs – operationId: uploadDocuments).
   * Request: UploadDocumentsRequest (files, taxYear); response: array of UploadUrlResponse.
   */
  getPresignedUrls: async (
    request: UploadDocumentsRequest
  ): Promise<UploadUrlResponse[]> => {
    const response = await apiClient.post<UploadUrlResponse[]>(
      API_ENDPOINTS.DOCS,
      request
    );
    return response.data;
  },

  /**
   * Request presigned URL for a single file. Converts PresignedUrlRequest to UploadDocumentsRequest.
   * @param taxYear - Tax year for the document (required by backend).
   */
  getPresignedUrl: async (
    request: PresignedUrlRequest,
    taxYear: number
  ): Promise<PresignedUrlResponse> => {
    const contentType = request.fileType || DEFAULT_CONTENT_TYPE;
    const uploadRequest: UploadDocumentsRequest = {
      files: [
        {
          name: request.fileName,
          type: contentType,
          size: `${(request.fileSize / 1024 / 1024).toFixed(2)}MB`,
        },
      ],
      taxYear,
    };
    const responses = await fileService.getPresignedUrls(uploadRequest);
    if (responses.length === 0) {
      throw new Error('No presigned URL returned');
    }
    const response = responses[0];
    const documentId = resolveDocumentId(response);
    return {
      uploadUrl: response.uploadUrl,
      documentId,
      key: response.key,
      name: response.name,
    };
  },

  /**
   * Step 2: Upload file directly to S3 using presigned URL
   */
  uploadToS3: async (
    presignedUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    const contentType = file.type || DEFAULT_CONTENT_TYPE;
    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': contentType,
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  },

  confirmUpload: async (documentId: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.DOC_CONFIRM_UPLOAD(documentId));
  },

  failUpload: async (documentId: string): Promise<void> => {
    try {
      await apiClient.post(API_ENDPOINTS.DOC_FAIL_UPLOAD(documentId));
    } catch {
      // Best-effort cleanup
    }
  },

  /**
   * Combined upload function: Get presigned URL + Upload to S3 + confirm.
   * @param taxYear - Tax year for the document (required by backend).
   */
  uploadFile: async (
    file: File,
    taxYear: number,
    onProgress?: (progress: number) => void
  ): Promise<{ documentId: string }> => {
    const contentType = file.type || DEFAULT_CONTENT_TYPE;
    const presignedResponse = await fileService.getPresignedUrl(
      {
        fileName: file.name,
        fileType: contentType,
        fileSize: file.size,
      },
      taxYear
    );

    const documentId = presignedResponse.documentId ?? '';

    try {
      await fileService.uploadToS3(presignedResponse.uploadUrl, file, onProgress);
      if (documentId) {
        await fileService.confirmUpload(documentId);
      }
      return { documentId };
    } catch (err) {
      if (documentId) {
        await fileService.failUpload(documentId);
      }
      throw err;
    }
  },

  /**
   * Get preview URL for a document (GET /api/docs/{documentId} – operationId: getDocumentPreview).
   * Returns DocumentPreviewResponse (url required; _id, name optional).
   */
  getPreviewUrl: async (documentId: string): Promise<FilePreviewResponse> => {
    const response = await apiClient.get<DocumentPreviewResponse>(
      API_ENDPOINTS.DOC_BY_ID(documentId)
    );
    return {
      url: response.data.url,
    };
  },

  /**
   * Delete a document (DELETE /api/docs/{documentId} – operationId: deleteDocument).
   * 200: success (no body).
   */
  deleteDocument: async (documentId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.DOC_BY_ID(documentId));
  },
};
